/**
 * CoordinateMapper - Centralized coordinate translation for screen share
 *
 * Handles conversions between:
 * - Client coordinates (mouse click position on screen)
 * - Normalized coordinates (0-1 range, resolution-independent)
 * - Native screen coordinates (actual desktop resolution)
 * - Display coordinates (video element display size)
 */
export class CoordinateMapper {
  constructor() {
    // Native desktop screen resolution (from desktop app)
    this.nativeWidth = null;
    this.nativeHeight = null;

    // Video element reference for display size calculations
    this.videoElement = null;
  }

  /**
   * Set the native desktop screen resolution
   * Called when desktop sends its screen dimensions
   */
  setNativeResolution(width, height) {
    this.nativeWidth = width;
    this.nativeHeight = height;
    // console.log(`[CoordinateMapper] Native resolution set: ${width}x${height}`);
  }

  /**
   * Set the video element for display calculations
   */
  setVideoElement(videoElement) {
    this.videoElement = videoElement;
  }

  /**
   * Get the current video dimensions and position
   * Accounts for object-fit: contain letterboxing
   * @returns {Object} { displayWidth, displayHeight, rect, videoWidth, videoHeight, renderedArea }
   */
  getVideoMetrics() {
    if (!this.videoElement) {
      return null;
    }

    const rect = this.videoElement.getBoundingClientRect();
    const videoWidth = this.videoElement.videoWidth;
    const videoHeight = this.videoElement.videoHeight;
    const nativeWidth = this.nativeWidth || videoWidth;
    const nativeHeight = this.nativeHeight || videoHeight;

    // Calculate the actual rendered video area (accounting for object-fit: contain)
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    const videoAspect = nativeWidth / nativeHeight;
    const containerAspect = containerWidth / containerHeight;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (videoAspect > containerAspect) {
      // Video is wider than container - letterbox top/bottom
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / videoAspect;
      offsetX = 0;
      offsetY = (containerHeight - renderedHeight) / 2;
    } else {
      // Video is taller than container - letterbox left/right
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * videoAspect;
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = 0;
    }

    return {
      // Container size (CSS pixels on screen)
      displayWidth: rect.width,
      displayHeight: rect.height,
      rect,
      // Intrinsic video dimensions (from stream)
      videoWidth,
      videoHeight,
      // Native screen dimensions (actual desktop)
      nativeWidth,
      nativeHeight,
      // Actual rendered video area (accounting for object-fit: contain)
      renderedArea: {
        width: renderedWidth,
        height: renderedHeight,
        offsetX,
        offsetY
      }
    };
  }

  /**
   * Convert client (mouse) coordinates to native screen coordinates
   * Accounts for object-fit: contain letterboxing
   * @param {number} clientX - Mouse X position
   * @param {number} clientY - Mouse Y position
   * @returns {Object} { screenX, screenY, normalizedX, normalizedY }
   */
  clientToScreen(clientX, clientY) {
    const metrics = this.getVideoMetrics();
    if (!metrics) {
      console.warn('[CoordinateMapper] No video element set');
      return null;
    }

    const { rect, nativeWidth, nativeHeight, renderedArea } = metrics;

    // Get click position relative to video element container
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    // Adjust for letterboxing offset to get position relative to actual video content
    const videoRelativeX = relativeX - renderedArea.offsetX;
    const videoRelativeY = relativeY - renderedArea.offsetY;

    // Check if click is within the actual video area (not in letterbox)
    if (videoRelativeX < 0 || videoRelativeX > renderedArea.width ||
        videoRelativeY < 0 || videoRelativeY > renderedArea.height) {
      console.warn('[CoordinateMapper] Click outside video content area (in letterbox)');
      return null;
    }

    // Calculate normalized coordinates (0-1) based on actual rendered video area
    const normalizedX = videoRelativeX / renderedArea.width;
    const normalizedY = videoRelativeY / renderedArea.height;

    // Convert to native screen coordinates
    const screenX = Math.round(normalizedX * nativeWidth);
    const screenY = Math.round(normalizedY * nativeHeight);

    return {
      screenX,
      screenY,
      normalizedX,
      normalizedY,
      relativeX,
      relativeY
    };
  }

  /**
   * Convert native screen coordinates to display coordinates
   * Used for rendering overlays on the video element
   * Accounts for object-fit: contain letterboxing
   * @param {number} screenX - Native screen X coordinate
   * @param {number} screenY - Native screen Y coordinate
   * @returns {Object} { displayX, displayY, scaleX, scaleY }
   */
  screenToDisplay(screenX, screenY) {
    const metrics = this.getVideoMetrics();
    if (!metrics) {
      return null;
    }

    const { nativeWidth, nativeHeight, renderedArea } = metrics;

    // Calculate scale factors based on actual rendered area
    const scaleX = renderedArea.width / nativeWidth;
    const scaleY = renderedArea.height / nativeHeight;

    return {
      // Add offset to account for letterboxing
      displayX: (screenX * scaleX) + renderedArea.offsetX,
      displayY: (screenY * scaleY) + renderedArea.offsetY,
      scaleX,
      scaleY
    };
  }

  /**
   * Convert a window bounds object from screen to display coordinates
   * Accounts for object-fit: contain letterboxing
   * @param {Object} bounds - { x, y, width, height } in screen coordinates
   * @returns {Object} Bounds in display coordinates
   */
  screenBoundsToDisplay(bounds) {
    const metrics = this.getVideoMetrics();
    if (!metrics || !bounds) {
      return null;
    }

    const { nativeWidth, nativeHeight, renderedArea } = metrics;

    // Calculate scale factors based on actual rendered area
    const scaleX = renderedArea.width / nativeWidth;
    const scaleY = renderedArea.height / nativeHeight;

    return {
      // Add offset to account for letterboxing
      x: (bounds.x * scaleX) + renderedArea.offsetX,
      y: (bounds.y * scaleY) + renderedArea.offsetY,
      width: bounds.width * scaleX,
      height: bounds.height * scaleY,
      scaleX,
      scaleY
    };
  }

  /**
   * Check if a point is within video bounds
   * @param {number} clientX - Mouse X position
   * @param {number} clientY - Mouse Y position
   * @returns {boolean}
   */
  isPointInVideo(clientX, clientY) {
    const metrics = this.getVideoMetrics();
    if (!metrics) {
      return false;
    }

    const { rect } = metrics;
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }
}

// Singleton instance for app-wide use
export const coordinateMapper = new CoordinateMapper();

export default CoordinateMapper;
